/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Generic structural sharing: deeply compares `prev` and `next`, reusing
 * `prev` references wherever the data is deeply equal. This prevents
 * unnecessary re-renders/re-evaluations downstream when object identity
 * is used for change detection (as in Jotai and React).
 *
 * Returns `prev` if the two are deeply equal, otherwise returns a new
 * object that reuses as many sub-references from `prev` as possible.
 */
export default function structuralShare<T>(prev: T, next: T): T {
  // Same reference — nothing to do
  if (prev === next) {
    return prev;
  }

  // Different types or either is null/undefined — use next
  if (
    prev == null ||
    next == null ||
    typeof prev !== typeof next
  ) {
    return next;
  }

  // Primitives — if they were equal, === would have caught it above
  if (typeof prev !== 'object') {
    return next;
  }

  // Arrays
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) {
      return next;
    }

    let allSame = true;
    const shared = prev.map((item, i) => {
      const result = structuralShare(item, (next as Array<unknown>)[i]);
      if (result !== item) {
        allSame = false;
      }
      return result;
    });

    return allSame ? prev : (shared as unknown as T);
  }

  // Plain objects
  if (!Array.isArray(prev) && !Array.isArray(next)) {
    const prevObj = prev as Record<string, unknown>;
    const nextObj = next as Record<string, unknown>;
    const prevKeys = Object.keys(prevObj);
    const nextKeys = Object.keys(nextObj);

    if (prevKeys.length !== nextKeys.length) {
      return next;
    }

    let allSame = true;
    const shared: Record<string, unknown> = {};

    for (const key of nextKeys) {
      if (!(key in prevObj)) {
        return next;
      }
      const result = structuralShare(prevObj[key], nextObj[key]);
      shared[key] = result;
      if (result !== prevObj[key]) {
        allSame = false;
      }
    }

    return allSame ? prev : (shared as T);
  }

  return next;
}
